import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import {
  NavigationConfiguration,
  NavigationLink,
  ToolbarConfiguration,
  ToolbarDropdownOption,
} from '@zipari/shared-ds-util-navigation';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { ExternalSiteModalConfig } from '../../external-site-warning-modal/external-site-warning-modal.model';
import * as actions from './ngrx-navigation.actions';
import { NavigationState } from './ngrx-navigation.reducers';
import * as selectors from './ngrx-navigation.selectors';

@Component({
  selector: 'ngrx-navigation',
  templateUrl: './ngrx-navigation.component.html',
})
export class NgrxNavigationComponent implements OnInit, OnChanges {
  @Input() config: NavigationConfiguration;
  @Input() toolbarConfig: ToolbarConfiguration;
  @Input() headerClass: { [key: string]: boolean };
  @Input() isLoggedIn: boolean;
  @Input() externalSiteModalConfig: ExternalSiteModalConfig;
  @Input() activeLangKey: string;
  @Output() dropdownOptionSelected: EventEmitter<any> = new EventEmitter();
  @Output() languageSwitched = new EventEmitter<string>();

  public config$: Observable<NavigationState> = this.store.pipe(
    select(selectors.selectNavigationConfig),
  );
  public toolbarConfig$: Observable<ToolbarConfiguration> = this.store.pipe(
    select(selectors.selectToolbarConfig),
  );
  public isMobileMenuOpen$: Observable<boolean> = this.store.pipe(
    select(selectors.selectIsMobileMenuOpen),
  );
  public openMenuItem$: Observable<NavigationLink> = this.store.pipe(
    select(selectors.selectOpenMenuItem),
  );
  public activeNavItem$: Observable<NavigationLink> = this.store.pipe(
    select(selectors.selectActiveNavItem),
  );
  public icons$: Observable<NavigationLink[]> = this.store.pipe(
    select(selectors.selectNavigationIcons),
  );

  constructor(private store: Store<NavigationState>) {}

  setToolbarConfig() {
    this.store.dispatch(
      actions.getToolbarConfiguration({
        payload: { config: this.toolbarConfig },
      }),
    );
  }

  ngOnInit(): void {
    this.store.dispatch(
      actions.getNavbarConfiguration({ config: this.config }),
    );
    this.setToolbarConfig();

    if (this.isLoggedIn) {
      this.getUnreadCounts();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isLoggedIn?.currentValue) {
      this.getUnreadCounts();
    }
    this.setToolbarConfig();
  }

  getUnreadCounts(): void {
    this.icons$.pipe(take(1)).subscribe((icons: NavigationLink[]) => {
      if (icons) {
        icons.forEach((icon: NavigationLink, index: number) => {
          if (icon.count_api) {
            this.store.dispatch(
              actions.getUnreadCountInit({
                payload: {
                  index,
                  endpoint: icon.count_api,
                },
              }),
            );
          }
        });
      }
    });
  }

  toggleNavMenu(link: NavigationLink): void {
    this.store.dispatch(actions.toggleNavbarMenu({ link }));
  }

  navItemClick(link: NavigationLink): void {
    // Don't set a parent nav item as the active item
    if (!link.items || link.items.length === 0) {
      this.store.dispatch(actions.setActiveNavLink({ link }));
    }
    this.toggleMobileMenu(false);
  }

  toolbarOptionClick(option: ToolbarDropdownOption): void {
    this.dropdownOptionSelected.emit(option);
    this.store.dispatch(
      actions.setActiveToolbarOption({ payload: { option } }),
    );
  }

  toggleMobileMenu(isOpen: boolean): void {
    this.store.dispatch(actions.toggleMobileMenu({ isOpen }));
  }

  logout(): void {
    this.toggleMobileMenu(false);
    this.store.dispatch(actions.logout());
  }

  handleLanguageSwitched(langKey: string) {
    this.languageSwitched.emit(langKey);
  }
}
